/**
 * Canvas renderer for a stylized avatar.
 *
 * Idle: the still avatar with breathing, sway and blinks (a closed-eyes sprite
 * generated from the avatar itself, cross-faded over the base image).
 * Talking: a pre-rendered answer video, green-keyed and cross-faded in place of
 * the still image. Videos are rendered from the same image, so they line up.
 */

import { ChromaKeyer } from "./chroma";

export type Box = [number, number, number, number];

export interface Sprite {
  sx: number;
  sy: number;
  w: number;
  h: number;
  x: number;
  y: number;
}

export interface FaceParts {
  width: number;
  height: number;
  boxes: { face: Box; left_eye: Box; right_eye: Box; mouth: Box };
  sprites?: { eyes_closed?: Sprite };
}

const BLINK_CLOSE_S = 0.06;
const BLINK_HOLD_S = 0.04;
const BLINK_OPEN_S = 0.1;
const FADE_S = 0.18; // still <-> video cross-fade

function smoothstep(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

export class AvatarRenderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly crop: { x: number; y: number; w: number; h: number };
  private readonly keyer = ChromaKeyer.create();

  private raf = 0;
  private last = 0;
  private blinkStart = -1;
  private nextBlink = 1.5;

  private video: HTMLVideoElement | null = null;
  private videoMix = 0; // 0 = still image, 1 = video
  private videoTarget = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly img: HTMLImageElement,
    private readonly parts: FaceParts,
    private readonly sheet: HTMLImageElement | null,
  ) {
    this.ctx = canvas.getContext("2d")!;
    this.crop = this.computeCrop();
  }

  start(): void {
    this.resize();
    const frame = (now: number) => {
      const t = now / 1000;
      const dt = this.last ? Math.min(0.05, t - this.last) : 0;
      this.last = t;
      this.update(t, dt);
      this.draw(t);
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
  }

  /** Show `video` (already playing or about to) until it ends or showStill() is called. */
  showVideo(video: HTMLVideoElement): void {
    this.video = video;
    this.videoTarget = 1;
  }

  showStill(): void {
    this.videoTarget = 0;
  }

  private computeCrop() {
    const [fx1, fy1, fx2, fy2] = this.parts.boxes.face;
    const fw = fx2 - fx1;
    const fh = fy2 - fy1;
    const aspect = this.canvas.clientHeight / this.canvas.clientWidth || 1.25;
    const w = Math.min(this.parts.width, fw * 2.1);
    const h = w * aspect;
    const x = Math.min(Math.max(0, (fx1 + fx2) / 2 - w / 2), this.parts.width - w);
    const y = Math.max(0, fy1 - fh * 0.42);
    return { x, y, w, h };
  }

  private update(t: number, dt: number): void {
    const step = dt / FADE_S;
    this.videoMix = this.videoTarget > this.videoMix ? Math.min(1, this.videoMix + step) : Math.max(0, this.videoMix - step);
    if (this.videoMix === 0 && this.videoTarget === 0) this.video = null;

    if (t >= this.nextBlink) {
      this.blinkStart = t;
      // Occasional double blink.
      this.nextBlink = t + (Math.random() < 0.15 ? 0.3 : 2.2 + Math.random() * 3.8);
    }
  }

  private blinkAmount(t: number): number {
    if (this.blinkStart < 0) return 0;
    const e = t - this.blinkStart;
    if (e < BLINK_CLOSE_S) return smoothstep(e / BLINK_CLOSE_S);
    if (e < BLINK_CLOSE_S + BLINK_HOLD_S) return 1;
    return 1 - smoothstep((e - BLINK_CLOSE_S - BLINK_HOLD_S) / BLINK_OPEN_S);
  }

  private draw(t: number): void {
    const { ctx, canvas, crop, img, parts } = this;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Work in avatar image coordinates from here on.
    const k = canvas.width / crop.w;
    ctx.setTransform(k, 0, 0, k, -crop.x * k, -crop.y * k);

    // Idle life: breathing (vertical scale from the bottom) and slow sway.
    // The videos have their own head motion, so idle motion fades out while they play.
    const idle = 1 - this.videoMix;
    const px = crop.x + crop.w / 2;
    const py = crop.y + crop.h;
    ctx.translate(px, py);
    ctx.rotate(idle * 0.005 * Math.sin((t * 2 * Math.PI) / 7.3));
    ctx.scale(1, 1 + idle * 0.007 * Math.sin((t * 2 * Math.PI) / 4.2));
    ctx.translate(-px, -py);

    const videoReady = this.video && this.videoMix > 0 && this.keyer?.draw(this.video);
    const mix = videoReady ? smoothstep(this.videoMix) : 0;

    if (mix < 1) {
      ctx.globalAlpha = 1 - mix;
      ctx.drawImage(img, 0, 0);
      const s = parts.sprites?.eyes_closed;
      const blink = this.blinkAmount(t);
      if (this.sheet && s && blink > 0.01) {
        ctx.globalAlpha = (1 - mix) * blink;
        ctx.drawImage(this.sheet, s.sx, s.sy, s.w, s.h, s.x, s.y, s.w, s.h);
      }
    }
    if (videoReady && mix > 0) {
      ctx.globalAlpha = mix;
      // The video is the whole avatar image, scaled down.
      ctx.drawImage(this.keyer!.canvas, 0, 0, parts.width, parts.height);
    }
    ctx.globalAlpha = 1;
  }
}
